"use client";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Group, Resource } from "@/db/schema";
import { createEvent } from "@/lib/createEvent";
import { authClient } from "@/lib/auth-client";
import { combineCallbacks, createToastCallbacks, withCallbacks } from "@/utils/withCallbacks";
import { useActionState, useRef, useState } from "react";
import SubmitButton from "../common/SubmitButton";
import { GenerateAiTitleButton } from "./GenerateAITitleButton";
import { renderDynamicFields } from "../dynamic-fields/renderDynamicFields";
import AttendeePicker from "./AttendeePicker";

interface CreateEventDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  resource: Resource & { group?: Group };
  startTime: Date;
  endTime: Date;
  setStartTime: (time: Date) => void;
  setEndTime: (time: Date) => void;
  /** Present only when this resource has an active Outlook room link. */
  attendeeMembers?: Array<{ email: string; name: string }>;
}

export function CreateEventDialog({
  open,
  setOpen,
  resource,
  startTime,
  endTime,
  setStartTime,
  setEndTime,
  attendeeMembers,
}: CreateEventDialogProps) {
  const { data: sessionData } = authClient.useSession();
  const user = sessionData?.user;

  const capitalize = (value: string) =>
    value ? value.charAt(0).toUpperCase() + value.slice(1) : "";
  const defaultAuthorName = capitalize(user?.name || user?.email?.split("@")?.[0] || "");
  const defaultAuthorCompany = capitalize(user?.email?.split("@")?.[1]?.split(".")?.[0] ?? "");

  // Add state for dynamic field values
  const [dynamicFieldValues, setDynamicFieldValues] = useState<Record<string, string>>({});

  // Handle dynamic field changes
  const handleDynamicFieldChange = (id: string, value: string) => {
    setDynamicFieldValues({
      ...dynamicFieldValues,
      [id]: value,
    });
  };

  const [, formAction, isPending] = useActionState(
    withCallbacks(
      createEvent,
      combineCallbacks(createToastCallbacks(), {
        onSuccess: () => {
          setOpen(false);
        },
      }),
    ),
    null,
  );

  const onChangeStartTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const s = new Date(startTime);
    s.setHours(parseInt(hours), parseInt(minutes));
    setStartTime(s);
  };

  const onChangeEndTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const e = new Date(endTime);
    e.setHours(parseInt(hours), parseInt(minutes));
    setEndTime(e);
  };

  const titleRef = useRef<HTMLInputElement>(null);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md">
        <form action={formAction}>
          <DialogHeader className="text-start space-y-0">
            <DialogTitle>
              Book <span className="text-blue-100">{resource.name}</span>
            </DialogTitle>
            <DialogDescription>
              <span className="capitalize">
                {startTime.toLocaleDateString("en-GB", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 md:gap-4 pt-8">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Title</Label>
              <div className="flex flex-row items-center space-x-2">
                <Input
                  ref={titleRef}
                  id="title"
                  name="title"
                  placeholder="ex. Morning meeting"
                  className="col-span-3"
                  required
                />
                <GenerateAiTitleButton
                  startTime={startTime}
                  endTime={endTime}
                  onTitleGenerated={(title) => {
                    if (titleRef.current) {
                      titleRef.current.value = title;
                    }
                  }}
                />
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="authorName">Booked by</Label>
              <Input
                id="authorName"
                required
                name="authorName"
                placeholder="ex. John Doe"
                className="col-span-3"
                defaultValue={defaultAuthorName}
              />
            </div>
            {attendeeMembers && <AttendeePicker members={attendeeMembers} />}
            {resource.group?.dynamicFields &&
              resource.group.dynamicFields.length > 0 &&
              resource.group?.dynamicFields.some((f) => f.target === "event") && (
                <div className="flex flex-col gap-2">
                  {renderDynamicFields(
                    resource.group.dynamicFields,
                    dynamicFieldValues,
                    handleDynamicFieldChange,
                  )}
                  <input
                    type="hidden"
                    name="dynamicFieldValues"
                    value={JSON.stringify(dynamicFieldValues)}
                  />
                </div>
              )}
            <div className="flex flex-col gap-2">
              <Label htmlFor="authorCompany">Company</Label>
              <Input
                id="authorCompany"
                name="authorCompany"
                placeholder="ex. E-corp"
                className="col-span-3"
                defaultValue={defaultAuthorCompany}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <div className="flex flex-col flex-1 min-w-[120px] gap-2">
                <Label htmlFor="startTime">Start time</Label>
                <Input
                  type="time"
                  placeholder=""
                  value={startTime.toTimeString().slice(0, 5)}
                  className="col-span-3"
                  onChange={(e) => {
                    onChangeStartTime(e.target.value);
                  }}
                />
                <input type="hidden" name="startTime" value={startTime.toISOString()} />
              </div>
              <div className="flex flex-col flex-1 min-w-[120px] gap-2">
                <Label htmlFor="endTime">End time</Label>
                <Input
                  type="time"
                  placeholder=""
                  value={endTime.toTimeString().slice(0, 5)}
                  className="col-span-3"
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!value || !/^(?:[01][0-9]|2[0-3]):[0-5][0-9]$/.test(value)) {
                      onChangeEndTime("");
                    } else {
                      onChangeEndTime(value);
                    }
                  }}
                />
                <input type="hidden" name="endTime" value={endTime.toISOString()} />
              </div>
            </div>
            <div className="flex flex-row gap-1 mb-4">
              <Button
                variant="accent"
                size="sm"
                type="button"
                onClick={() => {
                  setEndTime(new Date(startTime.getTime() + 30 * 60 * 1000));
                }}
              >
                30 min
              </Button>
              <Button
                variant="accent"
                size="sm"
                type="button"
                onClick={() => {
                  setEndTime(new Date(startTime.getTime() + 60 * 60 * 1000));
                }}
              >
                1 h
              </Button>
              <Button
                variant="accent"
                size="sm"
                type="button"
                onClick={() => {
                  setEndTime(new Date(startTime.getTime() + 90 * 60 * 1000));
                }}
              >
                1 h 30 min
              </Button>
              <Button
                variant="accent"
                size="sm"
                type="button"
                onClick={() => {
                  setEndTime(new Date(startTime.getTime() + 120 * 60 * 1000));
                }}
              >
                2 h
              </Button>
            </div>

            <input type="hidden" name="resourceId" value={resource.id} />
            <input type="hidden" name="date" value={startTime.toISOString()} />
            <SubmitButton
              size="lg"
              text="Create booking"
              isPending={isPending}
              className="w-full"
            />
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
